import { Carousel, Slide } from '@uhg-abyss/web/ui/Carousel';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSessionStorage } from 'usehooks-ts';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { maxPopupHeight, maxPopupWidth } from '../../utils/map.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { DataCardContainer } from '../DataCard/DataCardContainer';
import { DataCardStyle } from '../ResultSection/ResultSection.style';
import { MapCarousel } from './AmazonMapDisplay.styled';

type Data = {
  geometry?: Geomentry;
  id?: string;
  properties?: Properties;
};
type Geomentry = {
  coordinates?: string[];
  type?: string;
};
type Properties = {
  description: string;
  providerId: string;
  type: string;
};
type CarouselProps = {
  data: Data[];
  headers?: ResponseHeaders;
  selectLocation?: (locationId, locationLng, locationLat) => void;
  sectionType?: string;
  search?: string | undefined | null;
};

export const ClusterCarousel = ({
  data,
  headers,
  selectLocation,
  sectionType,
  search,
}: CarouselProps) => {
  const [mapviewEnhancements] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
  ]);

  const [popupContainer, setPopupContainer] = useState<any>(null);
  const [currentIndex, setCurrentindex] = useState(-1);
  const [, setHighlightId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_HIGHLIGHT_ID,
    { providerId: '', from: '' }
  );

  const handleHiglightId = (index: number) => {
    if (currentIndex >= 1 && data?.length && index <= data.length) {
      const currentItem = data[index - 1];
      if (!currentItem?.properties?.description) return;
      try {
        const parsed = JSON.parse(currentItem.properties.description);
        if (parsed?.providerId) {
          setHighlightId({ providerId: parsed.providerId, from: 'mapView' });
        }
      } catch {
        // skip highlight if description is not valid JSON
      }
    }
  };
  useEffect(() => {
    if (data) {
      const rootElem = document.querySelector(
        '.datacard-popup'
      ) as HTMLElement | null;

      const otherPopup = document.querySelector(
        '.map-viewport-constrained-popup'
      ) as HTMLElement | null;
      if (rootElem) {
        rootElem.style.width = mapviewEnhancements ? '151px' : 'fit-content';
        if (otherPopup) {
          const otherPopupContent = otherPopup.querySelector(
            '.non-provider-popup'
          ) as HTMLElement | null;
          const sizeTarget = otherPopupContent ?? otherPopup;
          rootElem.style.width = `${maxPopupWidth(sizeTarget)}px`;
          rootElem.style.height = `${maxPopupHeight(sizeTarget)}px`;
        }
        rootElem.style.visibility = 'visible';
      }

      handleHiglightId(1);
      setCurrentindex(1);
      setPopupContainer(rootElem);
    }
  }, [data]);

  useEffect(() => {
    handleHiglightId(currentIndex);
  }, [currentIndex]);

  const handlecurrentIndex = (type: string, index?: number) => {
    adobeLinkTrackEvent({
      name: type,
      location: `body:map:${sectionType} results for ${search} card`,
    });
    if (type === 'nextslide') {
      if (currentIndex < (data?.length ?? 0)) {
        setCurrentindex((prev) => prev + 1);
      }
    } else if (type === 'prevslide') {
      setCurrentindex((prev) => prev - 1);
    } else if (index != null) {
      setCurrentindex(index + 1);
    }
  };

  const handleDirectionClick = (provider) => {
    if (selectLocation)
      selectLocation(
        provider.locationId,
        provider.longitude,
        provider.latitude
      );
  };
  const safeParse = (value: string | undefined): Record<string, any> => {
    if (!value) return {};
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  };

  const slides = data?.map((item: any) => {
    const provider = safeParse(item?.properties?.description);
    return (
      <React.Fragment key={`mapcarousal${item.id}`}>
        <Slide>
          <DataCardContainer
            css={DataCardStyle}
            handleDirectionClick={() => handleDirectionClick(provider)}
            headers={headers}
            practitioner={provider}
            usedAt="Mapview"
          />
        </Slide>
      </React.Fragment>
    );
  });
  return (
    <React.Fragment>
      {popupContainer !== null &&
        createPortal(
          <MapCarousel>
            <Carousel
              data-auto-testid="map-cluster-carousel"
              data-testid="map-cluster-carousel"
              label=""
              nextSlideOnClick={() => handlecurrentIndex('nextslide')}
              prevSlideOnClick={() => handlecurrentIndex('prevslide')}
              slideIndexOnClick={(index) => {
                handlecurrentIndex('slideindex', index);
              }}
              slides={slides}
            />
          </MapCarousel>,
          popupContainer
        )}
    </React.Fragment>
  );
};
